import { useLocalStore } from 'mobx-react-lite';
import { useEffect } from 'react';

import { IcDeleteAdminIcon, IcOwnerIcon, IcSDeleteIcon, IcUserAdminIcon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { MenuItemStyle, IPopoverMenuItem } from '@gem/ui-kit';

import Tasks from 'APP/Tasks';
import { IWorkspaceUser } from 'APP/Tasks/workspace/getWorkspaceUsers';
import { WorkspaceRole } from 'APP/model/workspace/workspaceModel.types';
import Entities from 'STORE';
import { Workspace } from 'STORE/Workspace/Workspace';

export interface IWorkspaceMembersListItemPresenterParams {
  member: IWorkspaceUser;
  isShowMemberMenu: boolean;
  onMemberRemove(userId: string): void;
  onUpdateMemberRole(userId: string, role: WorkspaceRole): void;
  onReloadMembersList(): void;
  onMemberClick(member: IWorkspaceUser): void;
}

interface IWorkspaceMembersListItemPresenter {
  isShowMemberMenu: boolean;
  workspace: Workspace;
  member: IWorkspaceUser;
  isOwner: boolean;
  isAdmin: boolean;
  isMember: boolean;
  menuReference: HTMLElement | null;
  menuItems: IPopoverMenuItem[];
  canTransferOwnership: boolean;
  canAddToAdmins: boolean;
  canRemoveFromAdmins: boolean;
  canRemoveMember: boolean;
  isOpenedMenu: boolean;
  isMenuLoading: boolean;
  isShowMenu: boolean;
  setMember(member: IWorkspaceUser): void;
  transferOwnership(): Promise<void>;
  addToAdmins(): Promise<void>;
  removeFromAdmins(): Promise<void>;
  removeMember(): Promise<void>;
  onToggleMenu(element: HTMLElement): void;
  onCloseMenu(): void;
  onClick(): void;
  showErrorMessage(): void;
}

export function useWorkspaceMembersListItemPresenter({
  member,
  isShowMemberMenu,
  onMemberRemove,
  onUpdateMemberRole,
  onReloadMembersList,
  onMemberClick,
}: IWorkspaceMembersListItemPresenterParams): IWorkspaceMembersListItemPresenter {
  const { t } = useTranslation();

  const presenter = useLocalStore<IWorkspaceMembersListItemPresenter>(() => ({
    menuReference: null,
    isShowMemberMenu: true,
    isMenuLoading: false,
    member,

    setMember(member: IWorkspaceUser): void {
      presenter.member = member;
    },

    get workspace(): Workspace {
      return Entities.workspace;
    },

    get isShowMenu(): boolean {
      return presenter.isShowMemberMenu && presenter.menuItems.length > 0;
    },

    get isOwner(): boolean {
      return presenter.member.role === WorkspaceRole.OWNER;
    },

    get isAdmin(): boolean {
      return presenter.member.role === WorkspaceRole.ADMIN;
    },

    get isMember(): boolean {
      return presenter.member.role === WorkspaceRole.MEMBER;
    },

    async onClick(): Promise<void> {
      onMemberClick(presenter.member);
    },

    onToggleMenu(menuReference: HTMLElement): void {
      if (presenter.isOpenedMenu) {
        presenter.onCloseMenu();
        return;
      }
      presenter.menuReference = menuReference;
    },

    onCloseMenu(): void {
      this.menuReference = null;
    },

    get isOpenedMenu(): boolean {
      return !!presenter.menuReference;
    },

    get menuItems(): IPopoverMenuItem[] {
      return [
        {
          key: 'transferOwnership',
          isVisible: presenter.canTransferOwnership,
          title: t('space_transfer_ownership'),
          onClick: presenter.transferOwnership,
          icon: IcOwnerIcon,
        },
        {
          key: 'addToAdmins',
          isVisible: presenter.canAddToAdmins,
          title: t('add_to_admins'),
          onClick: presenter.addToAdmins,
          icon: IcUserAdminIcon,
        },
        {
          key: 'removeFromAdmins',
          isVisible: presenter.canRemoveFromAdmins,
          title: t('remove_from_admins'),
          onClick: presenter.removeFromAdmins,
          icon: IcDeleteAdminIcon,
        },
        {
          key: 'remove',
          isVisible: presenter.canRemoveMember,
          title: t('space_remove_user'),
          onClick: presenter.removeMember,
          icon: IcSDeleteIcon,
          style: MenuItemStyle.Danger,
        },
      ].filter((item) => item.isVisible);
    },

    get canTransferOwnership(): boolean {
      return presenter.workspace.role === WorkspaceRole.OWNER;
    },

    async transferOwnership(): Promise<void> {
      presenter.isMenuLoading = true;

      const canTransfer = await Tasks.app.showConfirm({
        text: t('common_transfer_ownership_alert', { 0: presenter.member.user.displayName }),
        primaryText: t('common_yes'),
        secondaryText: t('common_cancel'),
      });

      if (!canTransfer) {
        presenter.isMenuLoading = false;
        return;
      }

      const userId = presenter.member.user.id;
      const result = await Tasks.workspace.changeWorkspaceOwnership(userId);

      if (result) {
        onReloadMembersList();
      } else {
        presenter.showErrorMessage();
      }

      presenter.isMenuLoading = false;
    },

    get canAddToAdmins(): boolean {
      return presenter.workspace.role === WorkspaceRole.OWNER && presenter.isMember;
    },

    async addToAdmins(): Promise<void> {
      presenter.isMenuLoading = true;

      const canAddToAdmin = await Tasks.app.showConfirm({
        text: t('confirmation_text_add_admin', { 0: presenter.member.user.displayName }),
        primaryText: t('common_yes'),
        secondaryText: t('common_cancel'),
      });

      if (!canAddToAdmin) {
        presenter.isMenuLoading = false;
        return;
      }

      const userId = presenter.member.user.id;
      const result = await Tasks.workspace.promoteAdminToWorkspace([userId]);

      if (result) {
        onUpdateMemberRole(userId, WorkspaceRole.ADMIN);
      } else {
        presenter.showErrorMessage();
      }

      presenter.isMenuLoading = false;
    },

    get canRemoveFromAdmins(): boolean {
      return presenter.workspace.role === WorkspaceRole.OWNER && presenter.isAdmin;
    },

    async removeFromAdmins(): Promise<void> {
      const canRemove = await Tasks.app.showConfirm({
        text: t('workspace_remove_from_admins_alert'),
        primaryText: t('common_yes'),
        secondaryText: t('common_no'),
      });

      if (!canRemove) {
        return;
      }

      presenter.isMenuLoading = true;

      const userId = presenter.member.user.id;
      const result = await Tasks.workspace.revokeAdminFromWorkspace([userId]);

      if (result) {
        onUpdateMemberRole(userId, WorkspaceRole.MEMBER);
      } else {
        presenter.showErrorMessage();
      }

      presenter.isMenuLoading = false;
    },

    get canRemoveMember(): boolean {
      return (
        !presenter.isOwner &&
        (presenter.workspace.role === WorkspaceRole.OWNER ||
          (presenter.workspace.role === WorkspaceRole.ADMIN && presenter.isMember))
      );
    },

    async removeMember(): Promise<void> {
      const canRemove = await Tasks.app.showConfirm({
        text: t('workspace_remove_member_alert'),
        primaryText: t('common_yes'),
        secondaryText: t('common_no'),
      });

      if (!canRemove) {
        return;
      }

      presenter.isMenuLoading = true;

      const userId = presenter.member.user.id;

      const result = await Tasks.workspace.removeWorkspaceMember([userId]);

      if (result) {
        onMemberRemove(userId);
      } else {
        presenter.showErrorMessage();
      }

      presenter.isMenuLoading = false;
    },

    showErrorMessage(): void {
      Tasks.app.showToast(t('common_somethings_wrong_error'));
    },
  }));

  useEffect(() => {
    presenter.setMember(member);
  }, [member]);

  useEffect(() => {
    presenter.isShowMemberMenu = isShowMemberMenu;
  }, [isShowMemberMenu]);

  return presenter;
}
